import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import {
  Button,
  Dropdown,
  Avatar,
  ChatIcon,
  CalendarCheckIcon,
  CalendarEventIcon,
  CalendarOffIcon,
  GoogleCalendarIcon,
  CalendarIcon,
} from "@mwi/ui";

import { logout } from "slices/auth";
import { useLogoutMutation } from "api";
import { RootState } from "store";

const UnreadBadge = ({
  className,
  count,
}: {
  className?: string;
  count?: number;
}) => {
  if (count)
    return (
      <div
        className={classNames(
          "w-5 h-5 flex flex-row items-center justify-center rounded-full text-xs text-white bg-error-light",
          className,
        )}
      >
        {count}
      </div>
    );
  return null;
};

const MobileNavbar = ({
  practiceName,
  appointmentsBlocked,
  messagingBlocked,
  location,
  totalUnread,
}) => {
  return (
    <div
      className={classNames(
        "flex tablet:hidden flex-row px-4 h-[72px] w-full justify-between items-center",
        {
          hidden: location.pathname.replace(/\/+$/, "").includes("/messages/"),
        },
      )}
    >
      <h2 className="text-xl font-bold">{practiceName}</h2>

      <div className="flex flex-row justify-end gap-6 items-center">
        {!appointmentsBlocked && (
          <NavLink to="/appointments/book">
            <CalendarCheckIcon className="w-9 h-9" />
          </NavLink>
        )}
        {messagingBlocked === false && (
          <NavLink to="/messages">
            <div className="relative pr-2">
              <ChatIcon className="w-9 h-9" />
              <UnreadBadge
                className="absolute right-0 top-0"
                count={totalUnread}
              />
            </div>
          </NavLink>
        )}
      </div>
    </div>
  );
};

const DesktopNavbar = ({
  appointmentsBlocked,
  messagingBlocked,
  location,
  user,
  totalUnread,
}) => {
  const dispatch = useDispatch();
  const [submitLogout] = useLogoutMutation();

  const triggerLogout = async () => {
    await submitLogout();
    dispatch(logout());
  };

  return (
    <div className="hidden tablet:flex justify-end h-[72px]">
      <div className="flex items-center">
        {location.pathname !== "/home" &&
          location.pathname !== "/prescription-request" &&
          !appointmentsBlocked && (
            <Button type="link" to="/home" size="small" className="group">
              <div className="flex flex-row items-center gap-1">
                <CalendarCheckIcon className="w-5 h-5 text-on-primary" />
                Book an appointment
              </div>
            </Button>
          )}

        {messagingBlocked === false && (
          <Button
            className="ml-6 group"
            type="link"
            variant="outlined"
            to="/messages"
            size="small"
          >
            <div className="flex flex-row items-center gap-1">
              <ChatIcon className="w-5 h-5 text-primary group-hover:text-light" />
              Messages
              <UnreadBadge count={totalUnread} />
            </div>
          </Button>
        )}

        <div className="h-7 w-[1px] bg-pinball mx-8"></div>

        <Dropdown
          trigger={
            <Avatar
              name={`${user?.first_name ?? ""} ${user?.last_name ?? ""}`}
              className="w-10 h-10"
            />
          }
        >
          <Dropdown.Item onClick={triggerLogout}>Logout</Dropdown.Item>
        </Dropdown>
      </div>
    </div>
  );
};

export const HeaderNavbar = () => {
  const {
    user,
    appointmentsBlocked,
    messagingBlocked,
    practiceName,
    conversationsStore,
  } = useSelector((state: RootState) => ({
    user: state.auth.user,
    appointmentsBlocked: state.practice.appointments_blocked,
    messagingBlocked: state.practice.messaging_blocked,
    practiceName: state.practice.name,
    conversationsStore: state.messages.conversations,
  }));

  const conversations = useMemo(
    () => Object.values(conversationsStore),
    [conversationsStore],
  );
  const totalUnread = useMemo(
    () =>
      conversations.reduce(
        (accumulator, conversation) => accumulator + conversation.numUnread,
        0,
      ),
    [conversations],
  );

  if (
    location.pathname === "/appointments/book" ||
    location.pathname === "/prescriptions"
  ) {
    return null;
  }

  return (
    <nav className="w-full">
      <div className="border-b border-pinball">
        <MobileNavbar
          location={location}
          practiceName={practiceName}
          appointmentsBlocked={appointmentsBlocked}
          messagingBlocked={messagingBlocked}
          totalUnread={totalUnread}
        />
        <DesktopNavbar
          location={location}
          user={user}
          appointmentsBlocked={appointmentsBlocked}
          messagingBlocked={messagingBlocked}
          totalUnread={totalUnread}
        />
      </div>
    </nav>
  );
};
